import { ChevronRightIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import {
  FiCalendar,
  FiPause,
  FiPlay,
  FiSliders,
  FiTrash2,
} from "react-icons/fi";
import {
  cancelGoal,
  pauseGoal,
  resumeGoal,
  updateGoal,
} from "../../../../services/goalsApi";
import { trackEvent } from "../../../../shared/analytics";
import { formatShortDate, todayIso } from "../../helpers/goals";

const Row = ({ icon, title, description, onClick, danger, last }) => (
  <Flex
    as="button"
    align="center"
    borderBottom={last ? undefined : "1px solid"}
    borderColor="#30384E"
    gap="4"
    onClick={onClick}
    px="4"
    py="4"
    textAlign="left"
    type="button"
    w="100%"
    _hover={{ bg: "#222B45" }}
  >
    <Box color={danger ? "#FF708D" : "gray.300"} fontSize="22px">
      {icon}
    </Box>
    <Box flex="1" minW="0">
      <Text
        color={danger ? "#FF708D" : undefined}
        fontSize="lg"
        fontWeight="semibold"
      >
        {title}
      </Text>
      <Text color="gray.400" fontSize="sm">
        {description}
      </Text>
    </Box>
    {!danger && <ChevronRightIcon boxSize="6" color="gray.500" />}
  </Flex>
);

const standing = (goal) => {
  const progress = goal?.progress || {};
  if (goal?.type === "rate") {
    return `${progress.completed ?? 0} of ${
      progress.target ?? goal.targetCount
    } this ${goal.period === "week" ? "week" : "day"}`;
  }
  const base = `${progress.completed ?? 0} of ${
    progress.target ?? goal?.targetCount
  }`;
  const delta = Number(progress.delta) || 0;
  if (goal?.status === "paused") {
    return `${base} · paused`;
  }
  if (progress.paceStatus === "behind") {
    return `${base} · ${Math.abs(delta)} behind pace`;
  }
  if (progress.paceStatus === "ahead") {
    return `${base} · ${delta} ahead of pace`;
  }
  if (progress.paceStatus === "expired" || goal?.status === "expired") {
    return `${base} · deadline passed`;
  }
  return `${base} · on pace`;
};

/**
 * Four ways out, least to most destructive. Nothing here is a failure.
 */
const GoalManageModal = ({ goal, isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [step, setStep] = useState(null);
  const [endsOn, setEndsOn] = useState("");
  const [target, setTarget] = useState("");

  useEffect(() => {
    if (isOpen) {
      setStep(null);
      setEndsOn(goal?.progress?.suggestedEndDate || goal?.endsOn || "");
      setTarget(String(goal?.targetCount || ""));
    }
  }, [isOpen, goal]);

  const done = (title, description) => {
    queryClient.invalidateQueries(["goals"]);
    queryClient.invalidateQueries(["goalOrders"]);
    toast({
      title,
      description,
      status: "success",
      duration: 4000,
      isClosable: true,
    });
    onClose();
  };
  const fail = (error) =>
    toast({
      title: "Could not update the goal",
      description: error?.response?.data?.message || "Please try again.",
      status: "error",
      duration: 5000,
      isClosable: true,
    });

  const updateMutation = useMutation((changes) => updateGoal(goal.id, changes));
  const pauseMutation = useMutation(() => pauseGoal(goal.id));
  const resumeMutation = useMutation(() => resumeGoal(goal.id));
  const cancelMutation = useMutation(() => cancelGoal(goal.id));
  const busy =
    updateMutation.isLoading ||
    pauseMutation.isLoading ||
    resumeMutation.isLoading ||
    cancelMutation.isLoading;

  if (!goal) {
    return null;
  }

  const progress = goal.progress || {};
  const isDeadline = goal.type === "deadline";
  const isPaused = goal.status === "paused";
  const suggested = progress.suggestedEndDate;

  const track = (event) =>
    trackEvent(event, { goal_id: goal.id, goal_type: goal.type });

  const moveDate = () =>
    updateMutation
      .mutateAsync({ endsOn: endsOn || null })
      .then(() => track(endsOn ? "goal_date_moved" : "goal_deadline_removed"))
      .then(() =>
        done(
          endsOn
            ? `Deadline moved to ${formatShortDate(endsOn)}.`
            : "Deadline removed.",
          "Your reading progress is untouched."
        )
      )
      .catch(fail);

  const lowerTarget = () =>
    updateMutation
      .mutateAsync({ targetCount: Number(target) })
      .then(() => track("goal_target_lowered"))
      .then(() => done(`Target set to ${target}.`, "Progress is preserved."))
      .catch(fail);

  const togglePause = () =>
    (isPaused ? resumeMutation : pauseMutation)
      .mutateAsync()
      .then(() => track(isPaused ? "goal_resumed" : "goal_paused"))
      .then(() =>
        done(
          isPaused ? "Goal resumed." : "Goal paused.",
          isPaused
            ? "Pace tracking picks up from today."
            : "Elapsed days stop accruing until you resume."
        )
      )
      .catch(fail);

  const cancel = () =>
    cancelMutation
      .mutateAsync()
      .then(() => track("goal_cancelled"))
      .then(() =>
        done(
          "Goal cancelled.",
          "It moved to the archive. Your reading progress is untouched."
        )
      )
      .catch(fail);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent bg="#171C2D" border="1px solid" borderColor="#30384E">
        <ModalHeader pb="1">
          <Text fontSize="2xl" fontWeight="bold">
            {goal.title}
          </Text>
          <Text color="gray.400" fontSize="md" fontWeight="normal">
            {standing(goal)}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb="6">
          {step === null && (
            <>
              <Box
                border="1px solid"
                borderColor="#30384E"
                borderRadius="lg"
                overflow="hidden"
              >
                {isDeadline && !isPaused && (
                  <Row
                    description={
                      suggested
                        ? `${formatShortDate(
                            suggested
                          )} would put you back on pace`
                        : "Pick a new deadline"
                    }
                    icon={<FiCalendar />}
                    onClick={() => setStep("date")}
                    title="Move the date"
                  />
                )}
                {goal.type !== "yearly" && !isPaused && (
                  <Row
                    description={
                      isDeadline
                        ? "Aim for part of the order instead"
                        : "Pick a smaller number"
                    }
                    icon={<FiSliders />}
                    onClick={() => setStep("target")}
                    title="Lower the target"
                  />
                )}
                {goal.status !== "expired" && (
                  <Row
                    description={
                      isPaused
                        ? "Pace tracking picks up from today"
                        : "Stops pace tracking, keeps progress"
                    }
                    icon={isPaused ? <FiPlay /> : <FiPause />}
                    onClick={togglePause}
                    title={isPaused ? "Resume" : "Pause"}
                  />
                )}
                <Row
                  danger
                  description="Your reading progress is untouched"
                  icon={<FiTrash2 />}
                  last
                  onClick={() => setStep("cancel")}
                  title="Cancel goal"
                />
              </Box>
              <Text color="gray.400" fontSize="sm" mt="4">
                Cancelled goals move to an archive. Nothing is marked failed.
              </Text>
            </>
          )}

          {step === "date" && (
            <Box>
              <Text color="gray.400" fontSize="sm" mb="2">
                New deadline
              </Text>
              <Input
                aria-label="New deadline"
                min={todayIso()}
                onChange={(event) => setEndsOn(event.target.value)}
                type="date"
                value={endsOn}
              />
              <Flex gap="3" mt="4">
                <Button
                  onClick={() => setStep(null)}
                  variant="outline"
                  flex="1"
                >
                  Back
                </Button>
                <Button
                  colorScheme="red"
                  flex="1"
                  isDisabled={!endsOn}
                  isLoading={busy}
                  onClick={moveDate}
                >
                  Move the date
                </Button>
              </Flex>
              <Button
                mt="3"
                onClick={() => {
                  setEndsOn("");
                  updateMutation
                    .mutateAsync({ endsOn: null })
                    .then(() =>
                      done(
                        "Deadline removed.",
                        "Keep reading at your own pace."
                      )
                    )
                    .catch(fail);
                }}
                size="sm"
                variant="link"
                w="100%"
              >
                Keep reading without a deadline
              </Button>
            </Box>
          )}

          {step === "target" && (
            <Box>
              <Text color="gray.400" fontSize="sm" mb="2">
                {isDeadline
                  ? `Issues to finish (${progress.completed ?? 0} done so far)`
                  : `Comics a ${goal.period === "week" ? "week" : "day"}`}
              </Text>
              <NumberInput
                max={isDeadline ? progress.orderTotal || undefined : undefined}
                min={1}
                onChange={(value) => setTarget(value)}
                value={target}
              >
                <NumberInputField aria-label="New target" />
              </NumberInput>
              <Flex gap="3" mt="4">
                <Button
                  onClick={() => setStep(null)}
                  variant="outline"
                  flex="1"
                >
                  Back
                </Button>
                <Button
                  colorScheme="red"
                  flex="1"
                  isDisabled={!Number(target)}
                  isLoading={busy}
                  onClick={lowerTarget}
                >
                  Set target
                </Button>
              </Flex>
            </Box>
          )}

          {step === "cancel" && (
            <Box>
              <Text fontSize="lg" fontWeight="bold">
                Cancel this goal?
              </Text>
              <Text color="gray.400" fontSize="sm" mt="1">
                It moves to the archive. Your reading progress is untouched.
              </Text>
              <Flex gap="3" mt="4">
                <Button
                  onClick={() => setStep(null)}
                  variant="outline"
                  flex="1"
                >
                  Keep goal
                </Button>
                <Button
                  colorScheme="red"
                  flex="1"
                  isLoading={busy}
                  onClick={cancel}
                >
                  Cancel goal
                </Button>
              </Flex>
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default GoalManageModal;
